import { Code, Edit, MoreVertical, Play, Power, Settings, Trash2, Zap } from 'lucide-react';
import { Badge, Card, Dropdown, Menu, MenuItem, MenuDivider } from '@centraoneplus/ui';
import type { Capability } from '@services/integrations.service';

interface CapabilityCardProps {
  capability: Capability;
  onEdit: () => void;
  onTest: () => void;
  onDelete: () => void;
  onToggleStatus: () => void;
  /** No actions menu: the viewer may look but not change or run capabilities. */
  readOnly?: boolean;
}

const categoryIcons: Record<string, typeof Zap> = {
  ticket: Settings,
  customer: Settings,
  order: Settings,
  account: Settings,
  knowledge: Code,
  custom: Zap,
};

const NEUTRAL: React.CSSProperties = {
  borderColor: 'var(--co-line-strong)',
  background: 'var(--co-selected)',
  color: 'var(--co-ink-3)',
};

const statusStyles: Record<string, React.CSSProperties> = {
  active: {
    borderColor: 'var(--co-green-line)',
    background: 'var(--co-green-wash)',
    color: 'var(--co-green)',
  },
  disabled: NEUTRAL,
  deprecated: {
    borderColor: 'var(--co-terracotta-line)',
    background: 'var(--co-terracotta-wash)',
    color: 'var(--co-terracotta)',
  },
  beta: {
    borderColor: 'var(--co-brass)',
    background: 'var(--co-brass-wash)',
    color: 'var(--co-brass-ink)',
  },
};

const BRASS: React.CSSProperties = {
  borderColor: 'var(--co-brass)',
  background: 'var(--co-brass-wash)',
  color: 'var(--co-brass-ink)',
};

const CHIP =
  'co-mono inline-flex items-center whitespace-nowrap rounded-md border px-2 py-[3px] ' +
  'text-[9.5px] font-semibold uppercase tracking-[.1em]';

export function CapabilityCard({
  capability,
  onEdit,
  onTest,
  onDelete,
  onToggleStatus,
  readOnly = false,
}: CapabilityCardProps) {
  const Icon = categoryIcons[capability.category] || Zap;

  return (
    <Card
      className="!rounded-[10px] !border-[var(--co-line)] !bg-[var(--co-raised)] flex h-full flex-col px-[18px] py-4 [box-shadow:var(--co-shadow-card)] transition-transform hover:-translate-y-[1px]"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg border"
            style={capability.is_builtin ? BRASS : NEUTRAL}
          >
            <Icon className="h-[15px] w-[15px]" />
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              {capability.name}
            </h3>
            <p className="co-mono mt-1 truncate text-[10px] tracking-[.06em] text-[var(--co-ink-3)]">
              {capability.id}
            </p>
          </div>
        </div>

        {!readOnly && (
        <Dropdown
          trigger={
            <button className="rounded-md p-1 hover:bg-[var(--co-selected)]">
              <MoreVertical className="h-4 w-4 text-[var(--co-ink-3)]" />
            </button>
          }
          placement="bottom-end"
        >
          <Menu>
            <MenuItem onClick={onTest}>
              <Play className="h-4 w-4 mr-2" />
              Test
            </MenuItem>
            {(capability.status === 'active' || capability.status === 'disabled') && (
              <MenuItem onClick={onToggleStatus}>
                <Power className="h-4 w-4 mr-2" />
                {capability.status === 'active' ? 'Disable' : 'Enable'}
              </MenuItem>
            )}
            {!capability.is_builtin && (
              <>
                <MenuItem onClick={onEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </MenuItem>
                <MenuDivider />
                <MenuItem onClick={onDelete} className="text-[var(--co-burgundy)]">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </MenuItem>
              </>
            )}
          </Menu>
        </Dropdown>
        )}
      </div>

      <p className="mb-3 line-clamp-2 flex-1 text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
        {capability.description}
      </p>

      <div className="flex flex-wrap gap-2 mb-3">
        <span className={CHIP} style={statusStyles[capability.status] ?? NEUTRAL}>
          {capability.status}
        </span>
        {capability.is_builtin && (
          <span className={CHIP} style={BRASS}>
            Built-in
          </span>
        )}
        {capability.connector_id && (
          <span className={CHIP} style={NEUTRAL}>
            Connector
          </span>
        )}
      </div>

      {capability.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {capability.tags.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="default" className="text-xs">
              {tag}
            </Badge>
          ))}
          {capability.tags.length > 3 && (
            <Badge variant="default" className="text-xs">
              +{capability.tags.length - 3}
            </Badge>
          )}
        </div>
      )}

      <div className="co-mono mt-[13px] flex items-center justify-between border-t border-[var(--co-line-subtle)] pt-3 text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-4)]">
        <span>{capability.parameters.length} parameters</span>
        <span>v{capability.version}</span>
      </div>
    </Card>
  );
}
